/**
 * ReceiptQRDialog — shows the customer-facing receipt QR (links to the digital
 * receipt at /doc/{code}) right on screen, so staff no longer have to PRINT a
 * receipt just to reveal/scan its QR. Also offers a one-tap "Copy link" of the
 * same /doc/{code} URL. Plain portal overlay so it works reliably even when
 * opened from inside another dialog (e.g. the Sale Complete prompt).
 *
 * Props:
 *   open, onClose
 *   docCode    — existing doc code if already known (skips a fetch)
 *   invoiceId  — invoice/PO id; used to generate a code if docCode is missing
 *   invoiceNumber — shown as the heading
 *   docType    — 'invoice' | 'purchase_order' | 'branch_transfer' (default 'invoice')
 */
import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { api } from '../contexts/AuthContext';
import { QRCodeSVG } from 'qrcode.react';
import { QrCode, Copy, Check, RefreshCw, X, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ReceiptQRDialog({ open, onClose, docCode, invoiceId, invoiceNumber, docType = 'invoice' }) {
  const [code, setCode] = useState(docCode || '');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const receiptUrl = code ? `${window.location.origin}/doc/${code}` : '';

  useEffect(() => {
    if (!open) return;
    if (docCode) { setCode(docCode); return; }
    if (!invoiceId) return;
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const res = await api.post(`${BACKEND_URL}/api/doc/generate-code`, { doc_type: docType, doc_id: invoiceId });
        if (active) setCode(res.data?.code || '');
      } catch { if (active) toast.error('Could not generate receipt QR'); }
      if (active) setLoading(false);
    })();
    return () => { active = false; };
  }, [open, docCode, invoiceId, docType]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(receiptUrl);
    } catch {
      const el = document.createElement('textarea');
      el.value = receiptUrl; document.body.appendChild(el); el.select();
      document.execCommand('copy'); document.body.removeChild(el);
    }
    setCopied(true);
    toast.success('Receipt link copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.65)', zIndex: 99999, pointerEvents: 'auto' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      onPointerDown={e => e.stopPropagation()}
      onMouseDown={e => e.stopPropagation()}
      data-testid="receipt-qr-overlay">
      <div className="relative bg-white rounded-2xl shadow-2xl w-full overflow-y-auto"
        style={{ maxWidth: '340px', maxHeight: '90vh', pointerEvents: 'auto' }} data-testid="receipt-qr-dialog">
        <button onClick={(e) => { e.stopPropagation(); onClose(); }} data-testid="receipt-qr-close-x"
          className="absolute top-3 right-3 z-10 w-7 h-7 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center">
          <X size={14} className="text-slate-500" />
        </button>

        <div className="p-5">
          <div className="flex items-center gap-2 mb-4">
            <span className="inline-flex w-8 h-8 rounded-lg bg-[#1A4D2E] items-center justify-center shrink-0">
              <QrCode size={15} className="text-white" />
            </span>
            <div>
              <p className="font-semibold text-slate-800 text-sm leading-tight">Receipt QR</p>
              {invoiceNumber && <p className="text-[11px] text-slate-400">{invoiceNumber}</p>}
            </div>
          </div>

          {loading ? (
            <div className="flex flex-col items-center py-10 gap-2">
              <RefreshCw size={22} className="animate-spin text-slate-600" />
              <p className="text-xs text-slate-400">Preparing QR…</p>
            </div>
          ) : code ? (
            <div className="flex flex-col gap-3">
              <div className="flex flex-col items-center gap-2 py-1">
                <div style={{ border: '3px solid #1A4D2E', borderRadius: '12px', padding: '8px', background: '#fff', display: 'inline-block' }}>
                  <QRCodeSVG value={receiptUrl} size={160} level="M" fgColor="#1A4D2E" bgColor="#FFFFFF" data-testid="receipt-qr-image" />
                </div>
                <p className="text-xs text-slate-500 text-center">Scan to open the digital receipt on a phone</p>
              </div>

              <div className="flex gap-1.5 items-center">
                <div className="flex-1 min-w-0 bg-slate-100 rounded-lg px-2.5 py-1.5 text-[10px] text-slate-600 truncate" data-testid="receipt-qr-link">{receiptUrl}</div>
                <button onClick={copyLink} data-testid="receipt-qr-copy-btn"
                  className="shrink-0 w-8 h-8 rounded-lg bg-slate-100 hover:bg-slate-200 flex items-center justify-center" title="Copy link">
                  {copied ? <Check size={13} className="text-emerald-600" /> : <Copy size={13} className="text-slate-600" />}
                </button>
              </div>

              <a href={receiptUrl} target="_blank" rel="noreferrer" data-testid="receipt-qr-open"
                className="flex items-center justify-center gap-1.5 text-xs font-semibold text-[#1A4D2E] hover:underline">
                <ExternalLink size={12} /> Open receipt
              </a>
            </div>
          ) : (
            <p className="text-sm text-slate-400 text-center py-8">Receipt QR is not available for this document.</p>
          )}

          <button onClick={(e) => { e.stopPropagation(); onClose(); }} data-testid="receipt-qr-close-btn"
            className="w-full mt-4 py-2.5 rounded-xl border border-slate-200 text-sm text-slate-600 hover:bg-slate-50 transition-colors">
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
